import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Plus, ExternalLink, ShieldOff } from "lucide-react";
import { useConsent } from "@/context/ConsentContext";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { TUTORIAL_VIDEOS } from "@/data/content";

// Click-to-load facade: nothing is requested from YouTube until the viewer expands an item,
// and the player itself (youtube-nocookie) only loads on play.
const VideoFacade = ({ video, title, t, blocked }) => {
  const [playing, setPlaying] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const consent = useConsent();
  const allowed = consent.has("media") || unlocked;
  const watchUrl = `https://www.youtube.com/watch?v=${video.id}`;

  return (
    <div>
      <div className="relative aspect-video overflow-hidden rounded-2xl border border-[var(--line)] bg-black">
        {!allowed ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[var(--surface)] p-6 text-center" data-testid={`tutorial-blocked-${video.n}`}>
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[var(--line-strong)]">
              <ShieldOff className="h-5 w-5 text-[var(--sage)]" />
            </span>
            <div>
              <p className="font-semibold text-[var(--ink)]">{blocked.title}</p>
              <p className="mt-1 max-w-sm text-sm text-[var(--ink-2)]">{blocked.body}</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button type="button" onClick={() => setUnlocked(true)} className="btn btn-ghost px-4 py-2 text-sm" data-testid={`tutorial-unlock-once-${video.n}`}>
                {blocked.once}
              </button>
              <button
                type="button"
                onClick={() => consent.decide({ ...consent.choices, media: true })}
                className="btn btn-primary px-4 py-2 text-sm"
                data-testid={`tutorial-unlock-all-${video.n}`}
              >
                {blocked.allow}
              </button>
            </div>
          </div>
        ) : playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1&hl=de`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            data-testid={`tutorial-iframe-${video.n}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 flex h-full w-full items-center justify-center"
            aria-label={`${t.play}: ${title}`}
            data-testid={`tutorial-play-${video.n}`}
          >
            <img
              src={`https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`}
              onError={(e) => {
                if (!e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = "1";
                  e.currentTarget.src = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
                }
              }}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition-opacity duration-300 group-hover:opacity-100"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[var(--olive)] text-white shadow-[0_20px_50px_-15px_rgba(127,121,57,0.9)] transition-transform duration-300 group-hover:scale-105 sm:h-20 sm:w-20">
              <Play className="ml-1 h-7 w-7 fill-white sm:h-8 sm:w-8" />
            </span>
            <span className="absolute bottom-4 left-4 right-4 text-left font-mono text-[10px] uppercase tracking-widest text-white/70 sm:text-[11px]">
              {t.label} #{video.n} · {title}
            </span>
          </button>
        )}
      </div>
      <div className="mt-3 flex flex-col gap-2 text-xs text-[var(--ink-3)] sm:flex-row sm:items-center sm:justify-between">
        <span>{t.privacy}</span>
        <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[var(--ink-2)] transition-colors hover:text-white">
          {t.openOnYoutube}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
};

export const Tutorials = () => {
  const { t } = useLanguage();
  const s = t.tutorials;
  const [open, setOpen] = useState(0);

  return (
    <section id="tutorials" className="relative scroll-mt-24 py-24 md:py-32">
      <div className="hairline absolute inset-x-0 top-0 mx-auto max-w-5xl" />
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 md:px-8 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <SectionHeading index={s.index} eyebrow={s.eyebrow} title={s.title} body={s.body} />
          </div>
        </div>

        <div className="lg:col-span-8">
          <ul className="divide-y divide-[var(--line)] border-y border-[var(--line)]" data-testid="tutorials-list">
            {TUTORIAL_VIDEOS.map((video, i) => {
              const item = s.items[i];
              const isOpen = open === i;
              return (
                <li key={video.id}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    aria-controls={`tutorial-panel-${video.n}`}
                    data-testid={`tutorial-toggle-${video.n}`}
                    className="group flex w-full items-center gap-5 py-6 text-left"
                  >
                    <span className="w-8 shrink-0 font-mono text-xs text-[var(--olive-2)]">{String(video.n).padStart(2, "0")}</span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-lg font-semibold sm:text-xl ${isOpen ? "text-white" : "text-[var(--ink)]"}`}>{item.title}</span>
                      <span className="mt-1 block text-sm text-[var(--ink-3)]">{item.desc}</span>
                    </span>
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                        isOpen ? "rotate-45 border-[var(--sage)]/50 bg-[var(--olive)]/20" : "border-[var(--line-strong)] group-hover:border-[var(--sage)]/50"
                      }`}
                    >
                      {isOpen ? <Plus className="h-4 w-4 text-[var(--sage)]" /> : <Play className="ml-0.5 h-3.5 w-3.5 fill-[var(--sage)] text-[var(--sage)]" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={`tutorial-panel-${video.n}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-8 sm:pl-[3.25rem]">
                          <VideoFacade video={video} title={item.title} t={s} blocked={t.consent.blocked} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};
